import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Box,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Grid,
    Divider,
    CircularProgress
} from '@mui/material';
import { PersonAddOutlined, CloseOutlined } from '@mui/icons-material';

import { onboardEmployeeApi } from 'utils/API/Mis Apis/HrPortelApis';
import { handleCatchError } from 'utils/CatchHandler';
import { globalNotifyError } from 'views/utilities/AlertToast';

const initialOnboardState = {
    name: '',
    customer_email: '',
    mobile_no: '',
    dob: '',
    gender: '',
    pan_no: '',
    aadhar_no: '',
    account_no: '',
    monthly_credit_limit: '',
    agent_id: '',
    address: '',
    pincode: ''
};

const OnboardEmployeeModal = ({ open, onClose, onSuccess }) => {
    const [onboardForm, setOnboardForm] = useState(initialOnboardState);
    const [isOnboarding, setIsOnboarding] = useState(false);

    useEffect(() => {
        if (open) {
            setOnboardForm(initialOnboardState);
        }
    }, [open]);

    const handleOnboardChange = (field, value) => {
        setOnboardForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleClose = () => {
        if (!isOnboarding) {
            onClose();
        }
    };

    const handleOnboardSubmit = async (e) => {
        e.preventDefault();

        // Validations
        const mobile = onboardForm.mobile_no.trim();
        if (!mobile || !/^\d{10}$/.test(mobile)) {
            globalNotifyError('Please enter a valid 10-digit mobile number');
            return;
        }

        const accountNo = onboardForm.account_no.trim();
        if (!accountNo) {
            globalNotifyError('Please enter the employee bank account number');
            return;
        }
        if (!/^\d+$/.test(accountNo)) {
            globalNotifyError('Employee bank account number must contain only numbers');
            return;
        }

        if (!onboardForm.dob) {
            globalNotifyError('Please select date of birth (YYYY-MM-DD)');
            return;
        }

        const email = onboardForm.customer_email.trim();
        if (!email || !/\S+@\S+\.\S+/.test(email)) {
            globalNotifyError('Please enter a valid official email address');
            return;
        }

        const pan = onboardForm.pan_no.trim().toUpperCase();
        if (!pan || pan.length !== 10) {
            globalNotifyError('Please enter a valid 10-character PAN number');
            return;
        }

        const aadhar = onboardForm.aadhar_no.trim();
        if (!aadhar || aadhar.length < 12 || aadhar.length > 14 || !/^\d+$/.test(aadhar)) {
            globalNotifyError('Please enter a valid 12 to 14 digit Aadhar number');
            return;
        }

        if (onboardForm.pincode && !/^\d{6}$/.test(onboardForm.pincode.trim())) {
            globalNotifyError('Pincode must be a 6-digit number');
            return;
        }

        const payload = {
            mobile_no: mobile,
            account_no: accountNo,
            dob: onboardForm.dob,
            customer_email: email,
            pan_no: pan,
            aadhar_no: aadhar,
            ...(onboardForm.name?.trim() && { name: onboardForm.name.trim() }),
            ...(onboardForm.monthly_credit_limit !== '' && {
                monthly_credit_limit: Math.max(0, parseInt(onboardForm.monthly_credit_limit, 10) || 0)
            }),
            ...(onboardForm.gender && { gender: onboardForm.gender }),
            ...(onboardForm.address?.trim() && { address: onboardForm.address.trim() }),
            ...(onboardForm.pincode?.trim() && { pincode: onboardForm.pincode.trim() }),
            ...(onboardForm.agent_id !== '' &&
                !isNaN(Number(onboardForm.agent_id)) && {
                    agent_id: parseInt(onboardForm.agent_id, 10)
                })
        };

        setIsOnboarding(true);
        try {
            const response = await onboardEmployeeApi(payload);
            if (response?.data?.success) {
                onClose();
                setOnboardForm(initialOnboardState);
                if (onSuccess) onSuccess();
            } else if (response?.data?.message) {
            } else if (response?.response?.data?.message) {
            } else {
                onClose();
                setOnboardForm(initialOnboardState);
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            console.error('Error onboarding employee:', error);
            if (error?.response?.data?.message) {
            } else {
                handleCatchError(error);
            }
        } finally {
            setIsOnboarding(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: 1,
                    maxHeight: '92vh'
                }
            }}
        >
            <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#eff6ff', color: '#1e40af', display: 'flex' }}>
                        <PersonAddOutlined fontSize="small" />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Onboard Employee
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Register a new employee with corporate banking & initial credit limit
                        </Typography>
                    </Box>
                </Stack>
                <IconButton size="small" onClick={handleClose} disabled={isOnboarding}>
                    <CloseOutlined fontSize="small" />
                </IconButton>
            </DialogTitle>

            <form onSubmit={handleOnboardSubmit}>
                <DialogContent sx={{ pt: 1.5, pb: 2 }}>
                    <Stack spacing={2.5}>
                        {/* ── Section 1: Employee & Contact Details ── */}
                        <Box>
                            <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ mb: 1.5 }}>
                                1. Employee & Contact Information
                            </Typography>
                            <Grid container spacing={2}>
                                {/* Employee Name (Optional) */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Employee Full Name"
                                        fullWidth
                                        size="small"
                                        placeholder="e.g. John Doe"
                                        value={onboardForm.name}
                                        onChange={(e) => handleOnboardChange('name', e.target.value)}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>

                                {/* Official Email (Required) */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Official Email Address"
                                        required
                                        fullWidth
                                        size="small"
                                        type="email"
                                        placeholder="employee@corporate.com"
                                        value={onboardForm.customer_email}
                                        onChange={(e) => handleOnboardChange('customer_email', e.target.value)}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>

                                {/* Mobile Number (Required) */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Mobile Number"
                                        required
                                        fullWidth
                                        size="small"
                                        placeholder="9876543210"
                                        inputProps={{ maxLength: 10 }}
                                        value={onboardForm.mobile_no}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                            handleOnboardChange('mobile_no', val);
                                        }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">+91</InputAdornment>
                                        }}
                                        helperText="10-digit mobile number"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>

                                {/* Date of Birth (Required) */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Date of Birth"
                                        required
                                        fullWidth
                                        size="small"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={onboardForm.dob}
                                        onChange={(e) => handleOnboardChange('dob', e.target.value)}
                                        helperText="YYYY-MM-DD"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>

                                {/* Gender (Optional) */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="onboard-gender-label">Gender</InputLabel>
                                        <Select
                                            labelId="onboard-gender-label"
                                            label="Gender"
                                            value={onboardForm.gender}
                                            onChange={(e) => handleOnboardChange('gender', e.target.value)}
                                            sx={{ borderRadius: 2 }}
                                        >
                                            <MenuItem value="">Select Gender</MenuItem>
                                            <MenuItem value="MALE">Male</MenuItem>
                                            <MenuItem value="FEMALE">Female</MenuItem>
                                            <MenuItem value="OTHER">Other</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Master Agent ID (Optional) */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Master Agent ID (Optional)"
                                        fullWidth
                                        size="small"
                                        placeholder="Auto-defaults if omitted"
                                        value={onboardForm.agent_id}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '');
                                            handleOnboardChange('agent_id', val);
                                        }}
                                        helperText="Backend defaults to configured Master Agent if blank"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider />

                        {/* ── Section 2: KYC & Banking Information ── */}
                        <Box>
                            <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ mb: 1.5 }}>
                                2. KYC & Bank Account Details
                            </Typography>
                            <Grid container spacing={2}>
                                {/* PAN Number (Required) */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="PAN Number"
                                        required
                                        fullWidth
                                        size="small"
                                        placeholder="ABCDE1234F"
                                        inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
                                        value={onboardForm.pan_no}
                                        onChange={(e) => handleOnboardChange('pan_no', e.target.value.toUpperCase())}
                                        helperText="10-character PAN number"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>

                                {/* Aadhar Number (Required) */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Aadhar Number"
                                        required
                                        fullWidth
                                        size="small"
                                        placeholder="123456789012"
                                        inputProps={{ maxLength: 14 }}
                                        value={onboardForm.aadhar_no}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, 14);
                                            handleOnboardChange('aadhar_no', val);
                                        }}
                                        helperText="12 to 14 digit Aadhar number"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>

                                {/* Bank Account Number (Required) */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Employee Bank Account Number"
                                        required
                                        fullWidth
                                        size="small"
                                        placeholder="123456789012"
                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                        value={onboardForm.account_no}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '');
                                            handleOnboardChange('account_no', val);
                                        }}
                                        helperText="Numbers only — employee account for settlement/disbursements"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>

                                {/* Monthly Credit Limit (Optional, Default: 0.00) */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Monthly Credit Limit"
                                        fullWidth
                                        size="small"
                                        type="number"
                                        placeholder="50000"
                                        inputProps={{ min: 0, step: 1 }}
                                        value={onboardForm.monthly_credit_limit}
                                        onKeyDown={(e) => {
                                            if (['.', 'e', 'E', '+', '-'].includes(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        onChange={(e) => {
                                            const val = e.target.value.split('.')[0].replace(/\D/g, '');
                                            handleOnboardChange('monthly_credit_limit', val);
                                        }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">₹</InputAdornment>
                                        }}
                                        helperText="Initial monthly corporate credit limit (Default: ₹ 0)"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider />

                        {/* ── Section 3: Address Information (Optional) ── */}
                        <Box>
                            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1.5 }}>
                                3. Residential Address (Optional)
                            </Typography>
                            <Grid container spacing={2}>
                                {/* Address */}
                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        label="Residential Address"
                                        fullWidth
                                        size="small"
                                        placeholder="House / Flat No, Street, City, State"
                                        value={onboardForm.address}
                                        onChange={(e) => handleOnboardChange('address', e.target.value)}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>

                                {/* Pincode */}
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="Pincode"
                                        fullWidth
                                        size="small"
                                        placeholder="682001"
                                        inputProps={{ maxLength: 6 }}
                                        value={onboardForm.pincode}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                            handleOnboardChange('pincode', val);
                                        }}
                                        helperText="6-digit postal code"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, pt: 1, borderTop: '1px solid #f1f5f9' }}>
                    <Button
                        onClick={handleClose}
                        disabled={isOnboarding}
                        sx={{ borderRadius: 2, textTransform: 'none', color: 'text.secondary' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isOnboarding}
                        startIcon={isOnboarding ? <CircularProgress size={16} color="inherit" /> : <PersonAddOutlined />}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 3,
                            bgcolor: '#1e40af',
                            boxShadow: '0 4px 12px rgba(30, 64, 175, 0.25)',
                            '&:hover': { bgcolor: '#1d4ed8' }
                        }}
                    >
                        {isOnboarding ? 'Onboarding...' : 'Onboard Employee'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

OnboardEmployeeModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func
};

export default OnboardEmployeeModal;
